// Dylan's `paper.json` → a Revise run, profile `paper`.
//
// This is the one-step change to the caps-history-interactive skill: instead
// of `build.py` merging paper.json into an engine folder for Vercel, the skill
// hands the same file to the MCP, which maps it here and validates it. The
// mapping is field for field (content-schema.md is the source of the names on
// the left), so nothing about how Dylan authors changes.
//
// Image sources: paper.json references files that sit next to it
// (`<img class='sp-img' src='1D.png'>`). The caller uploads those files and
// passes {filename → hosted image} in `images`; the mapper rewrites the
// reference and strips the tag from the body.

import type { NscSkill, QuestionScreen, ReviseImage, ReviseRun, ReviseScreen, ReviseSource } from './types';

export interface PaperJsonSource {
  label: string;
  type: string;
  html: string;
  attr: string;
}

export interface PaperJsonQuestion {
  id: string;
  src: string;
  num: string;
  m: number;
  lv: 1 | 2 | 3;
  ll: string;
  t: string;
  opts: string[];
  c: number | number[];
  fb: string;
  multi?: boolean;
  mx?: number;
}

export interface PaperJsonMta {
  label: string;
  text: string;
  expLv?: number;
  expM?: string;
  crit: Array<{ q: string; cor: 'yes' | 'partial' | 'no'; exp: string }>;
}

export interface PaperJson {
  meta: {
    title: string;
    subtitle?: string;
    paperRef: string;
    bigQuestion?: string;
    totalMarks: number;
    recommendedMinutes?: number;
    realExamMinutes?: number;
    interactiveId?: string;
    paper?: string;
    section?: string;
    questionNumber?: number;
    guideExamples?: Partial<Record<NscSkill, string>>;
  };
  sources: Record<string, PaperJsonSource>;
  questions: PaperJsonQuestion[];
  mta: PaperJsonMta[];
}

/** The three synthetic source ids Dylan's schema requires. */
const SYNTHETIC = new Set(['compare', 'para', 'mta']);

export interface PaperToRunOptions {
  /** Hosted images keyed by the filename paper.json references (e.g. "1D.png"). */
  images?: Record<string, Omit<ReviseImage, 'id'> & { id?: string }>;
  slug?: string;
  accent?: string;
}

const IMG_RE = /<img\b[^>]*\bsrc=['"]([^'"]+)['"][^>]*>/gi;

/**
 * The sources a comparison panel names in its label ("Sources 1A + 1B"), in
 * label order. Only keys that exist among `sources` count. Used at import, and
 * by the player for runs saved before `ReviseSource.compares` existed.
 */
export function comparedKeys(label: string, sources: Pick<ReviseSource, 'key'>[]): string[] {
  const esc = (k: string) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return sources
    .map((s) => ({ key: s.key, at: label.search(new RegExp(`(^|[^A-Za-z0-9])${esc(s.key)}(?![A-Za-z0-9])`)) }))
    .filter((m) => m.at >= 0)
    .sort((a, b) => a.at - b.at)
    .map((m) => m.key);
}

/** The full sources a comparison panel points at: `compares` when set, else the label. */
export function comparedSources(source: ReviseSource, all: ReviseSource[]): ReviseSource[] {
  const others = all.filter((s) => s.key !== source.key);
  const keys = source.compares ?? (source.key === 'compare' || /compar/i.test(source.type ?? '') ? comparedKeys(source.label, others) : []);
  return keys.map((k) => others.find((s) => s.key === k)).filter((s): s is ReviseSource => !!s);
}

/** "Sources 1A and 1B" (or "Sources 1A, 1B and 1C") for a comparison's chip. */
export function comparisonLabel(compared: Pick<ReviseSource, 'key'>[]): string {
  const keys = compared.map((s) => s.key);
  const list = keys.length < 2 ? keys.join('') : `${keys.slice(0, -1).join(', ')} and ${keys[keys.length - 1]}`;
  return `Sources ${list}`;
}

function skillOf(ll: string): NscSkill | undefined {
  const s = ll.trim().toLowerCase() as NscSkill;
  return (['extraction', 'concept', 'interpretation', 'analysis', 'reliability', 'comparison', 'synthesis'] as NscSkill[]).includes(s) ? s : undefined;
}

/** Results bands on the DBE seven-level scale. Generic copy; an author may edit in the builder. */
export function defaultPaperBands(): ReviseRun['results']['bands'] {
  return [
    { min: 80, label: 'Level 7', text: 'Outstanding. Your source skills are exam-ready. Keep the timing tight and move to the next paper.' },
    { min: 70, label: 'Level 6', text: 'Meritorious. Strong source skills; refine evaluation and comparison for full marks.' },
    { min: 60, label: 'Level 5', text: 'Substantial. Finding and explaining are secure. Work on the judging questions.' },
    { min: 50, label: 'Level 4', text: 'Adequate. Re-read the feedback on every wrong answer, then take the paper again.' },
    { min: 40, label: 'Level 3', text: 'Moderate. Go back to the sources and the skills guides before you retry.' },
    { min: 30, label: 'Level 2', text: 'Elementary. Start with the extraction questions and build up.' },
    { min: 0, label: 'Level 1', text: 'Not achieved yet. Work through one source at a time with the guide open.' },
  ];
}

export function paperToRun(paper: PaperJson, opts: PaperToRunOptions = {}): ReviseRun {
  const images: ReviseImage[] = [];
  const imageIdByFile = new Map<string, string>();
  for (const [file, img] of Object.entries(opts.images ?? {})) {
    const id = img.id ?? file.replace(/\.[a-z0-9]+$/i, '').replace(/[^a-z0-9_-]/gi, '-');
    imageIdByFile.set(file, id);
    images.push({ ...img, id });
  }

  const sources: ReviseSource[] = [];
  const missingImages: string[] = [];
  for (const [key, s] of Object.entries(paper.sources)) {
    if (SYNTHETIC.has(key)) continue;
    let imageId: string | undefined;
    const body = s.html.replace(IMG_RE, (_m, src: string) => {
      const file = src.split('/').pop() ?? src;
      // By filename first; else by stem, which is how an image already hosted
      // by a previous import (known only by its id) satisfies the reference.
      const stem = file.replace(/\.[a-z0-9]+$/i, '').replace(/[^a-z0-9_-]/gi, '-');
      const id = imageIdByFile.get(file) ?? (images.some((i) => i.id === stem) ? stem : undefined);
      if (id) imageId = id;
      else missingImages.push(file);
      return '';
    });
    sources.push({
      key,
      label: s.label,
      type: s.type,
      body: body.trim() || undefined,
      imageId,
      attribution: s.attr,
    });
  }
  // The comparison panel is a real source in the run so questions can hang off it.
  if (paper.sources.compare) {
    const c = paper.sources.compare;
    const compares = comparedKeys(c.label, sources);
    sources.push({ key: 'compare', label: c.label, type: c.type, body: c.html.replace(IMG_RE, ''), attribution: c.attr, ...(compares.length ? { compares } : {}) });
  }

  const screens: ReviseScreen[] = [];
  let lastSource: string | null = null;
  const sourceKeys = new Set(sources.map((s) => s.key));

  for (const q of paper.questions) {
    const srcKey = q.src === 'para' || q.src === 'mta' ? undefined : q.src;
    if (srcKey && srcKey !== lastSource && sourceKeys.has(srcKey)) {
      const src = sources.find((x) => x.key === srcKey)!;
      const compared = comparedSources(src, sources);
      screens.push({ id: `src-${srcKey}`, type: 'source', sourceKey: srcKey, tag: compared.length ? comparisonLabel(compared) : `Source ${srcKey}` });
      lastSource = srcKey;
    }
    if (q.src === 'para' && lastSource !== 'para') {
      const p = paper.sources.para;
      screens.push({ id: 'para-intro', type: 'content', tag: p?.label ?? 'Paragraph', html: p?.html ?? '<p>Build the paragraph one move at a time.</p>' });
      lastSource = 'para';
    }
    const isMulti = q.multi === true || (Array.isArray(q.c) && q.multi !== false && q.mx !== undefined);
    const question: QuestionScreen = {
      id: q.id,
      type: 'question',
      mode: isMulti ? 'multi' : 'mcq',
      assessed: true,
      tag: `L${q.lv}`,
      skill: skillOf(q.ll),
      marks: q.m,
      short: `Q ${q.num}`,
      number: q.num,
      stem: q.t,
      options: q.opts,
      correct: q.c,
      feedback: q.fb,
      // Kept even when it matches no source: the validator blocks on the
      // dangling reference, which is how a typo in `src` gets caught.
      sourceKey: srcKey,
    };
    if (isMulti) question.maxSelect = q.mx ?? (Array.isArray(q.c) ? q.c.length : 1);
    else if (Array.isArray(q.c) && q.c.length > 1) question.acceptAny = true;
    screens.push(question);
  }

  if (paper.mta?.length) {
    const intro = paper.sources.mta;
    screens.push({ id: 'mta-intro', type: 'mta-intro', heading: intro?.label ?? 'Mark This Answer', html: intro?.html ?? '' });
    paper.mta.forEach((m, i) => {
      screens.push({
        id: `mta-${i + 1}`,
        type: 'mta',
        label: m.label,
        script: m.text,
        criteria: m.crit.map((c, j) => ({ id: `c${j + 1}`, prompt: c.q, correct: c.cor, feedback: c.exp })),
        band: m.expM && !m.expM.startsWith('?') ? m.expM : undefined,
      });
    });
  }

  const lines = [paper.meta.bigQuestion, paper.meta.subtitle].filter((x): x is string => !!x && x.trim().length > 0);
  const run: ReviseRun = {
    version: 1,
    profile: 'paper',
    board: 'nsc',
    slug: opts.slug ?? paper.meta.interactiveId,
    accent: opts.accent,
    cover: {
      eyebrow: paper.meta.paperRef,
      title: paper.meta.title,
      lines,
      begin: 'Begin',
      time: paper.meta.recommendedMinutes
        ? `About ${paper.meta.recommendedMinutes} minutes. ${paper.meta.totalMarks} marks.`
        : `${paper.meta.totalMarks} marks.`,
      skippable: false,
    },
    screens,
    results: {
      eyebrow: 'Your paper results',
      title: 'Where you stand',
      bands: defaultPaperBands(),
    },
    sources,
    images: images.length ? images : undefined,
    paper: {
      paperRef: paper.meta.paperRef,
      bigQuestion: paper.meta.bigQuestion,
      totalMarks: paper.meta.totalMarks,
      recommendedMinutes: paper.meta.recommendedMinutes,
      realExamMinutes: paper.meta.realExamMinutes,
      guideExamples: paper.meta.guideExamples,
      timed: true,
    },
  };

  // Surface unresolved image files where the validator will see them: an
  // image the body referenced but nobody uploaded is a source with no body.
  if (missingImages.length) {
    run.images = run.images ?? [];
    (run as ReviseRun & { _missingImages?: string[] })._missingImages = missingImages;
  }
  return run;
}

/** Filenames paper.json references that were not supplied as hosted images. */
export function missingImageFiles(run: ReviseRun): string[] {
  return (run as ReviseRun & { _missingImages?: string[] })._missingImages ?? [];
}

/**
 * Shape check deep enough that `paperToRun` cannot throw on its input: every
 * field the mapper reads without a guard is checked here, so a wrong file
 * fails with a sentence from `paperJsonIssues`, not a TypeError.
 */
export function paperJsonIssues(v: unknown): string[] {
  const issues: string[] = [];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return ['paper.json must be a JSON object.'];
  const p = v as Record<string, unknown>;
  const meta = p.meta as Record<string, unknown> | undefined;
  if (!meta || typeof meta !== 'object') issues.push('meta is required.');
  else {
    if (typeof meta.title !== 'string' || !meta.title.trim()) issues.push('meta.title is required.');
    if (typeof meta.paperRef !== 'string' || !meta.paperRef.trim()) issues.push('meta.paperRef is required.');
    if (typeof meta.totalMarks !== 'number') issues.push('meta.totalMarks must be a number.');
  }
  const sources = p.sources as Record<string, unknown> | undefined;
  if (!sources || typeof sources !== 'object' || Array.isArray(sources)) issues.push('sources must be an object keyed by source id.');
  else {
    for (const [key, s] of Object.entries(sources)) {
      const src = s as Record<string, unknown> | null;
      if (!src || typeof src !== 'object') issues.push(`sources.${key} is not an object.`);
      else {
        if (typeof src.html !== 'string') issues.push(`sources.${key}.html must be a string.`);
        if (typeof src.label !== 'string') issues.push(`sources.${key}.label must be a string.`);
        if (typeof src.attr !== 'string') issues.push(`sources.${key}.attr must be a string.`);
      }
    }
  }
  if (!Array.isArray(p.questions)) issues.push('questions must be an array.');
  else {
    p.questions.forEach((q, i) => {
      const qq = q as Record<string, unknown> | null;
      if (!qq || typeof qq !== 'object') return issues.push(`questions[${i}] is not an object.`);
      for (const f of ['id', 'src', 'num', 't', 'fb', 'll'] as const) {
        if (typeof qq[f] !== 'string') issues.push(`questions[${i}].${f} must be a string.`);
      }
      if (typeof qq.m !== 'number') issues.push(`questions[${i}].m must be a number.`);
      if (![1, 2, 3].includes(qq.lv as number)) issues.push(`questions[${i}].lv must be 1, 2 or 3.`);
      if (!Array.isArray(qq.opts) || !qq.opts.every((o) => typeof o === 'string')) issues.push(`questions[${i}].opts must be an array of strings.`);
      if (typeof qq.c !== 'number' && !(Array.isArray(qq.c) && qq.c.every((c) => typeof c === 'number'))) issues.push(`questions[${i}].c must be a number or an array of numbers.`);
    });
  }
  if (p.mta !== undefined) {
    if (!Array.isArray(p.mta)) issues.push('mta must be an array.');
    else {
      p.mta.forEach((m, i) => {
        const mm = m as Record<string, unknown> | null;
        if (!mm || typeof mm !== 'object') return issues.push(`mta[${i}] is not an object.`);
        if (typeof mm.label !== 'string') issues.push(`mta[${i}].label must be a string.`);
        if (typeof mm.text !== 'string') issues.push(`mta[${i}].text must be a string.`);
        if (!Array.isArray(mm.crit)) issues.push(`mta[${i}].crit must be an array.`);
        else {
          mm.crit.forEach((c, j) => {
            const cc = c as Record<string, unknown> | null;
            if (!cc || typeof cc.q !== 'string' || typeof cc.exp !== 'string' || !['yes', 'partial', 'no'].includes(cc.cor as string)) {
              issues.push(`mta[${i}].crit[${j}] needs q, exp and cor (yes | partial | no).`);
            }
          });
        }
      });
    }
  }
  return issues;
}

export function isPaperJson(v: unknown): v is PaperJson {
  return paperJsonIssues(v).length === 0;
}
